import settings from '@/../settings.json';
import { usePartnerScoreDataAvailabilityCompetitor } from '@/api/hcp';
import { useUpdateUserProfile } from '@/api/queries/userProfile/useUpdateUserProfile';
import WelcomePopover from '@/components/filter/ActiveFiltersContainer/WelcomePopover/WelcomePopover';
import ManageSavedFiltersDrawer from '@/components/filter/ManageSavedFilters/ManageSavedFiltersDrawer';
import SavedFiltersDropdown from '@/components/filter/SavedFiltersDropdown/SavedFiltersDropdown';
import SharedViewDialog from '@/components/filter/SharedViewDialog/SharedViewDialog';
import AdobeAnalyticsContext from '@/context/AdobeAnalyticsContext';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { useFeatureFlag } from '@/context/FeatureToggleContext/FeatureToggleContext';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { useAvoidBackgroundScrolling, useUserProfile } from '@/hooks';
import useApplyUserFilter from '@/hooks/useApplyUserFilter';
import useSavedFilters from '@/hooks/useSavedFilters';
import Button from '@/storybook/components/Button/Button';
import If from '@/storybook/components/If/If';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import overlayStyles from '@/styles/shared/overlay.module.css';
import { NavbarEntry, NavigationSettings } from '@/types/base';
import { Period } from '@/types/date';
import { translateDynamicKey } from '@/types/translation';
import { AppRoutes } from '@/utils';
import { Filter } from '@cubejs-client/core';
import cx from 'classnames';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Navigation from '../Navigation/Navigation';
import NumberSelectedHcps from '../NumberSelectedHcps/NumberSelectedHcps';
import NavEntry from './Component/NavEntry';
import SelectedDateRange from './Component/SelectedDateRange';
import styles from './NavigationBar.module.scss';
import {
  applyAggregatedScientificInformationComingSoon,
  excludeDisabledEntryIds,
  updateNestedMenuPartnerScore,
} from './NavigationBar.utils';
import {
  EXCLUDE_HCP_SELECTION_ROUTES,
  isPathExcluded,
  useIsDateRangeVisible,
  useIsFilterComponentVisible,
} from './routeExclusions';
import { TestIds } from './testids';

interface NavigationBarProps {
  tierFilter?: Filter;
  ppocFilter?: Filter;
}

const NavigationBar: FC<NavigationBarProps> = ({ tierFilter, ppocFilter }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const analytics = useContext(AdobeAnalyticsContext);
  const { setFilterOverlayVisible, activeFilters, filterState } = useContext(FiltersContext);
  const user = useUserProfile();
  const { mutate: updateUserProfile } = useUpdateUserProfile();
  const { data: competitor = [], isLoading: isCompetitorLoading } = usePartnerScoreDataAvailabilityCompetitor();
  const dateRangePeriod = filterState.dateRange as Period;
  const previousDateRangePeriod = filterState.comparisonPeriod as Period;

  const { filters: allSavedFilters, recentFilters, refetch: refetchFilters } = useSavedFilters();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { applyFilter, isApplying, validationDialogProps } = useApplyUserFilter({ filters: allSavedFilters });

  const filtersVisible = activeFilters.length > 0;
  const showWelcomeMessage = !user.welcomePageIsAccepted && !filtersVisible;

  useAvoidBackgroundScrolling(showWelcomeMessage);

  const onCloseWelcomeMessage = useCallback(() => {
    updateUserProfile({
      userId: user.userId,
      welcomePageIsAccepted: true,
    });
  }, [updateUserProfile, user?.userId]);

  const isAggregatedScientificInformationEnabled = useFeatureFlag('aggregatedScientificInformation');
  const isSalesEnabled = useFeatureFlag('sales');
  const isDateRangeVisible = useIsDateRangeVisible(location.pathname, location.search);
  const isFilterComponentVisible = useIsFilterComponentVisible(location.pathname, location.search);

  const navEntries = useMemo(() => {
    const navSettings = settings.navigation as NavigationSettings;
    const entryIds = excludeDisabledEntryIds(navSettings.order, { SALES_GROUP: isSalesEnabled });

    return entryIds.map((id: string) => {
      const entry = navSettings.entries[id];
      const route = AppRoutes[id as keyof typeof AppRoutes];
      const label = translateDynamicKey(t, entry.label);

      if (id === 'PARTNERSCORE') {
        return updateNestedMenuPartnerScore(id, entry, label, competitor || !isCompetitorLoading);
      }
      if (id === 'AGGREGATED_ENGAGEMENT') {
        return applyAggregatedScientificInformationComingSoon(
          { ...entry, route, label },
          isAggregatedScientificInformationEnabled,
        );
      }
      return { ...entry, route, label };
    });
  }, [competitor, isCompetitorLoading, isAggregatedScientificInformationEnabled, isSalesEnabled, t]);

  const handleOneDataLayer = useCallback(
    (entry: NavbarEntry) => {
      analytics?.trackLinkClick({
        innertext: entry.label,
        type: AdobeAnalyticsConst.LINK_TYPES.TABS,
        position: AdobeAnalyticsConst.POSITIONS.NAVIGATION_TABS,
        name: AdobeAnalyticsConst.LINKCLICK_NAMES.DASHBOARD_NAVIGATION_TABS,
      });
    },
    [analytics],
  );

  const showFilterButtonClick = useCallback(() => {
    analytics?.trackLinkClick({
      innertext: AdobeAnalyticsConst.BUTTON_TEXT.SHOW_FILTERS,
      type: AdobeAnalyticsConst.LINK_TYPES.BUTTON,
      position: AdobeAnalyticsConst.POSITIONS.NAVIGATION_BAR,
      name: AdobeAnalyticsConst.LINKCLICK_NAMES.SHOW_FILTERS,
    });
    if (showWelcomeMessage) {
      onCloseWelcomeMessage();
    }
    setFilterOverlayVisible(true);
  }, [analytics, showWelcomeMessage, setFilterOverlayVisible, onCloseWelcomeMessage]);

  const testIdMapping = {
    'ces-top-bar-naventry-aggregated': 'ces-top-bar-naventry-aggregated',
    'ces-top-bar-naventry-customerfocus': 'ces-top-bar-naventry-customerfocus',
    'ces-top-bar-naventry-hcps': 'ces-top-bar-naventry-hcps',
    'ces-top-bar-naventry-partnerscore': 'ces-top-bar-naventry-partnerscore',
  };

  return (
    <div className={styles.navigationBar}>
      <Navigation>
        <ul>
          {navEntries.map((navEntry, index) => (
            <li key={navEntry.label} onClick={() => handleOneDataLayer(navEntry)}>
              <NavEntry
                entry={navEntry}
                testId={
                  testIdMapping[navEntry.id as keyof typeof testIdMapping] || `${TestIds.topBar.menuItem}-${index + 1}`
                }
              />
            </li>
          ))}
        </ul>
        <div className={styles.navRight}>
          <If condition={!isPathExcluded(EXCLUDE_HCP_SELECTION_ROUTES, location.pathname)}>
            <div className={styles.hcpSelectedWrapper} data-testid={TestIds.topBar.usercounter}>
              <NumberSelectedHcps tierFilter={tierFilter} ppocFilter={ppocFilter} />
            </div>
          </If>
          <If condition={isDateRangeVisible}>
            <SelectedDateRange dateRangePeriod={dateRangePeriod} previousDateRangePeriod={previousDateRangePeriod} />
          </If>
          <If condition={isFilterComponentVisible}>
            {showWelcomeMessage && <div className={styles.highlighter} />}
            <WelcomePopover
              onClose={onCloseWelcomeMessage}
              showWelcomeMessage={showWelcomeMessage}
              horizontalOffset={-150}
              className={cx({ [styles.welcomePopover]: showWelcomeMessage })}
              anchor={
                <Button size="L" onClick={showFilterButtonClick} data-testid={TestIds.topBar.filter}>
                  {t('header.navigation.showFilter')}
                </Button>
              }
            />
            <SavedFiltersDropdown
              filters={recentFilters}
              onApplyFilter={applyFilter}
              onViewAll={() => setIsDrawerOpen(true)}
            />
          </If>
        </div>
      </Navigation>

      <ManageSavedFiltersDrawer
        isOpen={isDrawerOpen}
        filters={allSavedFilters}
        onClose={() => setIsDrawerOpen(false)}
        onFiltersChanged={refetchFilters}
      />

      {isApplying &&
        createPortal(
          <div className={overlayStyles.fullPageLoader} data-testid="apply-filter-loader">
            <LoadingSpinner />
          </div>,
          document.body,
        )}

      {validationDialogProps && <SharedViewDialog {...validationDialogProps} />}
    </div>
  );
};

export default NavigationBar;
